/**
 * 游客账号管理
 * 页面加载时自动创建或获取游客账号
 */

(function () {
  "use strict";

  // 配置 - 从全局配置获取 API 基础 URL
  const GUEST_CONFIG = {
    baseUrl: window.THEME_CONFIG?.apiBaseUrl || "https://alb.deliver3d.net:3000",
    storageKey: "deliver3d_guest_token",
    requestTimeoutMs: 10000,
  };

  const GUEST_TEST_CONFIG = {
    enabled: window.THEME_CONFIG?.pageType === "index",
    baseUrl: "https://plb.zmkj.site",
    endpoint: "/auth/tempUserAutoLogin",
    requestTimeoutMs: 10000,
  };

  const isValidToken = (token) =>
    typeof token === "string" &&
    token.trim() !== "" &&
    token !== "undefined" &&
    token !== "null";

  // 游客账号对象
  const GuestAccount = {
    initPromise: null,
    lastInitError: null,

    // 初始化
    async init() {
      if (this.initPromise) {
        return this.initPromise;
      }

      this.initPromise = this.initInternal().finally(() => {
        this.initPromise = null;
      });

      return this.initPromise;
    },

    async initInternal() {
      try {
        // 检查是否已有有效的token
        const existingToken = this.getStoredToken();

        if (existingToken) {
          window.guestToken = existingToken;
          this.lastInitError = null;
          return { token: existingToken };
        }

        // 创建新的游客账号
        const token = await this.retry(() => this.createTourist(), 2, 600);

        if (token) {
          window.guestToken = token;
          this.lastInitError = null;
          return { token };
        }
      } catch (error) {
        this.lastInitError = error;
        this.trackCreationFailed(error);
        return null;
      }
    },

    // 创建游客账号
    async createTourist() {
      const result = await this.runGuestStep(
        "temp_user_auto_login",
        "/auth/tempUserAutoLogin",
        () => this.tempUserAutoLogin(),
      );
      const token = result?.token;
      if (!isValidToken(token)) {
        const error = new Error("No token received from temp user auto login");
        error.guestStep = "temp_user_auto_login";
        error.guestEndpoint = "/auth/tempUserAutoLogin";
        throw error;
      }

      this.storeToken(token);
      return token;
    },

    // API: 创建游客并自动登录
    async tempUserAutoLogin() {
      const response = await this.fetchWithTimeout(
        `${GUEST_CONFIG.baseUrl}/auth/tempUserAutoLogin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Temp user auto login failed: ${response.statusText}`);
      }

      return await response.json();
    },

    async fetchWithTimeout(url, options = {}) {
      if (typeof AbortController === "undefined") {
        return fetch(url, options);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, GUEST_CONFIG.requestTimeoutMs);

      try {
        return await fetch(url, {
          ...options,
          signal: controller.signal,
        });
      } catch (error) {
        if (error && error.name === "AbortError") {
          throw new Error(`Request timeout: ${url}`);
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }
    },

    async runGuestStep(step, endpoint, task) {
      try {
        return await task();
      } catch (error) {
        error.guestStep = error.guestStep || step;
        error.guestEndpoint = error.guestEndpoint || endpoint;
        throw error;
      }
    },

    trackCreationFailed(error) {
      try {
        const params = {
          guest_step: String(error?.guestStep || this.inferGuestStep(error)).slice(0, 100),
          guest_endpoint: String(error?.guestEndpoint || this.inferGuestEndpoint(error)).slice(0, 200),
          error_name: String(error?.name || "Error").slice(0, 100),
          error_message: String(error?.message || "").slice(0, 300),
          has_stored_token: this.getStoredToken() ? "yes" : "no",
        };

        if (window.GtagUtils && typeof window.GtagUtils.sendEvent === "function") {
          window.GtagUtils.sendEvent("guest_creation_failed", params);
        } else if (typeof window.gtag === "function") {
          window.gtag("event", "guest_creation_failed", params);
        }
      } catch (trackingError) {}
    },

    inferGuestStep(error) {
      const message = String(error?.message || "").toLowerCase();
      if (
        message.includes("/auth/tempuserautologin") ||
        message.includes("temp user auto login")
      ) {
        return "temp_user_auto_login";
      }
      return "unknown";
    },

    inferGuestEndpoint(error) {
      const endpointMap = {
        temp_user_auto_login: "/auth/tempUserAutoLogin",
      };
      return endpointMap[this.inferGuestStep(error)] || "";
    },

    // 从本地存储获取token
    getStoredToken() {
      try {
        const token = localStorage.getItem(GUEST_CONFIG.storageKey);
        if (!isValidToken(token)) {
          localStorage.removeItem(GUEST_CONFIG.storageKey);
          return null;
        }
        return token;
      } catch (error) {
        return null;
      }
    },

    // 存储token到本地
    storeToken(token) {
      if (!isValidToken(token)) return;
      try {
        localStorage.setItem(GUEST_CONFIG.storageKey, token);
      } catch (error) {
        // Silent fail
      }
    },

    // 清除账号
    clearAccount() {
      try {
        localStorage.removeItem(GUEST_CONFIG.storageKey);
        delete window.guestToken;
      } catch (error) {
        // Silent fail
      }
    },

    // 获取Token
    getToken() {
      return isValidToken(window.guestToken) ? window.guestToken : this.getStoredToken();
    },

    async retry(task, retries = 2, delayMs = 600) {
      let lastError = null;
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          return await task();
        } catch (error) {
          lastError = error;
          if (attempt >= retries) break;
          await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
        }
      }
      throw lastError;
    },
  };

  const GuestAccountTestProbe = {
    hasRun: false,

    async run() {
      if (!GUEST_TEST_CONFIG.enabled || this.hasRun) return;
      this.hasRun = true;

      const startedAt = Date.now();
      const endpoint = GUEST_TEST_CONFIG.endpoint;
      const url = `${GUEST_TEST_CONFIG.baseUrl}${endpoint}`;

      try {
        const response = await this.fetchWithTimeout(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });

        let hasToken = "unknown";
        try {
          const result = await response.clone().json();
          hasToken = isValidToken(result?.token) ? "yes" : "no";
        } catch (error) {
          hasToken = "parse_failed";
        }

        this.track("success", {
          endpoint,
          status_code: response.status,
          response_ok: response.ok ? "yes" : "no",
          has_token: hasToken,
          duration_ms: Date.now() - startedAt,
        });
      } catch (error) {
        this.track("failed", {
          endpoint,
          status_code: "",
          response_ok: "no",
          has_token: "unknown",
          error_name: String(error?.name || "Error").slice(0, 100),
          error_message: String(error?.message || "").slice(0, 300),
          duration_ms: Date.now() - startedAt,
        });
      }
    },

    async fetchWithTimeout(url, options = {}) {
      if (typeof AbortController === "undefined") {
        return fetch(url, options);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, GUEST_TEST_CONFIG.requestTimeoutMs);

      try {
        return await fetch(url, {
          ...options,
          signal: controller.signal,
        });
      } catch (error) {
        if (error && error.name === "AbortError") {
          throw new Error(`Request timeout: ${url}`);
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }
    },

    track(result, params) {
      try {
        const eventParams = {
          test_result: result,
          test_base_url: GUEST_TEST_CONFIG.baseUrl,
          test_endpoint: params.endpoint || GUEST_TEST_CONFIG.endpoint,
          status_code: String(params.status_code ?? "").slice(0, 20),
          response_ok: String(params.response_ok || "no"),
          has_token: String(params.has_token || "unknown"),
          duration_ms: Number(params.duration_ms || 0),
          page_type: window.THEME_CONFIG?.pageType || "",
          error_name: String(params.error_name || "").slice(0, 100),
          error_message: String(params.error_message || "").slice(0, 300),
        };

        if (window.GtagUtils && typeof window.GtagUtils.sendEvent === "function") {
          window.GtagUtils.sendEvent("guest_creation_probe", eventParams);
        } else if (typeof window.gtag === "function") {
          window.gtag("event", "guest_creation_probe", eventParams);
        }
      } catch (trackingError) {}
    },
  };

  // 先暴露全局对象，避免自动初始化期间上传模块无法主动等待/复用 init。
  window.GuestAccount = GuestAccount;

  // 页面加载完成后立即初始化
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      GuestAccount.init();
      GuestAccountTestProbe.run();
    });
  } else {
    // DOM 已经加载完成
    GuestAccount.init();
    GuestAccountTestProbe.run();
  }
})();
